import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash, LockKey, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem("mm-admin-key") || "");
  const [input, setInput] = useState(key);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/messages`, { headers: { "X-Admin-Key": k } });
      setMessages(res.data);
    } catch (e) {
      setError(e.response?.status === 401 ? "Invalid admin key." : "Could not load messages.");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  const submitKey = (e) => {
    e.preventDefault();
    sessionStorage.setItem("mm-admin-key", input);
    setKey(input);
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: { "X-Admin-Key": key } });
      setMessages((m) => m.filter((x) => x.id !== id));
      toast.success("Message deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-void text-zinc-100 px-6 py-10 md:py-16">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-gold transition-colors duration-300">
            <ArrowLeft size={16} /> Back to site
          </Link>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">MARKMAXO · Inbox</p>
        </div>

        <h1 className="font-display text-3xl md:text-4xl font-bold mb-8">Contact Messages</h1>

        {!key || error ? (
          <form onSubmit={submitKey} className="max-w-md rounded-2xl border border-white/10 bg-surface p-8 space-y-4" data-testid="admin-key-form">
            <div className="flex items-center gap-3 text-gold">
              <LockKey size={22} weight="duotone" />
              <p className="text-sm font-semibold">Enter your admin key to view the inbox.</p>
            </div>
            {error && <p role="alert" data-testid="admin-error" className="text-sm text-red-400">{error}</p>}
            <input
              type="password"
              data-testid="admin-key-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin key"
              className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm outline-none focus:border-gold/60"
              required
            />
            <button type="submit" data-testid="admin-key-submit" className="w-full rounded-full bg-gold px-6 py-3 text-sm font-semibold text-void hover:bg-gold-soft transition-colors duration-300">
              Open Inbox
            </button>
          </form>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-zinc-500" data-testid="admin-message-count">
                {messages.length} message{messages.length === 1 ? "" : "s"}
              </p>
              <button onClick={() => load(key)} data-testid="admin-refresh-button" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-gold transition-colors duration-300">
                <ArrowsClockwise size={15} /> Refresh
              </button>
            </div>
            {loading ? (
              <p className="text-zinc-500 text-sm">Loading…</p>
            ) : messages.length === 0 ? (
              <p className="text-zinc-500 text-sm" data-testid="admin-empty-state">No messages yet. New enquiries from the contact form will appear here.</p>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full text-sm" data-testid="admin-messages-table">
                  <thead>
                    <tr className="border-b border-white/10 bg-surface text-left font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4">Name</th>
                      <th className="px-5 py-4">Email</th>
                      <th className="px-5 py-4">Project</th>
                      <th className="px-5 py-4">Message</th>
                      <th className="px-5 py-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((m) => (
                      <tr key={m.id} className="border-b border-white/[0.06] hover:bg-white/[0.02]" data-testid={`admin-message-row-${m.id}`}>
                        <td className="px-5 py-4 whitespace-nowrap text-zinc-500">{new Date(m.timestamp).toLocaleString()}</td>
                        <td className="px-5 py-4 font-medium text-zinc-100">{m.name}</td>
                        <td className="px-5 py-4 text-gold">{m.email}</td>
                        <td className="px-5 py-4 whitespace-nowrap text-zinc-400">{m.project_type}</td>
                        <td className="px-5 py-4 text-zinc-400 max-w-sm whitespace-pre-wrap">{m.message}</td>
                        <td className="px-5 py-4">
                          <button onClick={() => remove(m.id)} data-testid={`admin-delete-${m.id}`} aria-label="Delete message" className="text-zinc-600 hover:text-red-400 transition-colors duration-300">
                            <Trash size={17} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
